import styles from './PaymentManageDetailPage.module.scss'
import clsx from 'clsx'
import { useMemo, useState } from 'react'
import PaymentStatus from '../../components/paymentStatus/paymentStatus'
import { ArrowLeftIcon, BellAlertIcon } from '@heroicons/react/24/solid'
import { useNavigate, useParams, Link } from 'react-router-dom'

import TourDisplay from '../../components/tourDisplay/TourDisplay'
import DepositDefaultModal from './DepositDefaultModal'
import { useBankAccount, useDisburseSettlement, useSettlementDetail, useUser } from '../../hooks'

const PAYMENT_STATUS_MAP = {
	SETTLEMENT_PENDING: '정산전',
	WAITING_FOR_PAYMENT: '입금대기',
	PAYMENT_COMPLETED: '입금완료',
	PAYMENT_DISBURSED: '지급완료',
}

const PERIOD_CONTRACT_TYPES = new Set(['year_second_half', 'year_first_half', 'both'])

const BANK_CODE_LABEL_MAP = {
	'002': 'KDB산업은행',
	'003': 'IBK기업은행',
	'004': 'KB국민은행',
	'007': '수협은행',
	'011': 'NH농협은행',
	'020': '우리은행',
	'023': 'SC제일은행',
	'027': '한국씨티은행',
	'031': 'iM뱅크(대구은행)',
	'032': '부산은행',
	'034': '광주은행',
	'035': '제주은행',
	'039': '새마을금고',
	'045': '경남은행',
	'071': '우체국',
	'081': '하나은행(KEB하나은행)',
	'088': '신한은행',
	'089': '케이뱅크',
	'090': '카카오뱅크',
}

const formatNumber = (value) => {
	if (value === null || value === undefined) return null
	const number = Number(value)
	if (Number.isNaN(number)) return value
	return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const getStatusDetailMessage = (status) => {
	switch (status) {
		case '정산전':
			return '대회 상금과 용역비 등 총액을 아직 계산하기 전이에요'
		case '입금대기':
			return '정산금액이 나왔어요. 입금하기를 눌러 입금해주세요!'
		case '입금완료':
			return '입금이 성공적으로 완료되었어요. 곧 캐디에게 지급됩니다!'
		case '지급완료':
			return '캐디에게 지급이 완료되었어요!'
		default:
			return ''
	}
}

const resolveProfileTypeLabel = (profileType) => {
	if (!profileType) return ''
	const normalized = String(profileType).toLowerCase()
	if (normalized === 'tourcaddie') return '투어캐디'
	if (normalized === 'housecaddie') return '하우스캐디'
	if (normalized === 'tourpro') return '투어프로'
	return ''
}

const formatExpireDeadline = (value) => {
	if (!value) return '-'

	const parsed = new Date(value)
	if (Number.isNaN(parsed.getTime())) {
		return '-'
	}

	const dayNames = ['일', '월', '화', '수', '목', '금', '토']
	const year = parsed.getFullYear()
	const month = String(parsed.getMonth() + 1).padStart(2, '0')
	const day = String(parsed.getDate()).padStart(2, '0')
	const dayLabel = dayNames[parsed.getDay()]

	return `${year}년 ${month}월 ${day}일(${dayLabel})까지`
}

const buildAmountInfo = (paymentData, contract = {}, contractTour = {}) => {
	if (!paymentData) {
		return {
			totalPrize: null,
			totalPayment: null,
			weeklySalaryBase: null,
			weeklySalaryPayable: null,
			weeklySalary: null,
			incentive: null,
			proCommission: null,
			proCommissionAmount: null,
			proVat: null,
			proTotalPayment: null,
			periodSalary: null,
			jejuSupport: null,
			overseasSupport: null,
			withholdingTaxAmount: null,
			caddieCommissionAmount: null,
			caddieVatAmount: null,
			caddieFinalReceiveAmount: null,
		}
	}

	const tour = contractTour.tour ?? {}
	const statusCode =
		paymentData?.paymentStatus ??
		paymentData?.status ??
		paymentData?.contractPayment?.paymentStatus
	const allowFallbackForSupports = statusCode !== 'SETTLEMENT_PENDING'

	const totalPrize =
		paymentData.totalPrize ?? contractTour.prize_money ?? tour.prize_money ?? tour.purse ?? null

	return {
		totalPrize,
		totalPayment:
			paymentData.totalPayment ??
			paymentData.caddieFinalReceiveAmount ??
			paymentData.proFinalPaymentAmount ??
			null,
		weeklySalaryBase:
			paymentData.weeklySalaryBase ??
			contract?.domesticContract?.weeklySalaryCutThrough ??
			null,
		weeklySalaryPayable: paymentData.weeklySalaryPayable ?? null,
		weeklySalary: paymentData.weeklySalaryBase ?? paymentData.weeklySalaryPayable ?? null,
		incentive: paymentData.incentive ?? null,
		proCommission: paymentData.proCommissionAmount ?? null,
		proCommissionAmount: paymentData.proCommissionAmount ?? null,
		proVat: paymentData.proVat ?? null,
		proTotalPayment: paymentData.proFinalPaymentAmount ?? null,
		periodSalary: paymentData.periodSalary ?? null,
		jejuSupport: (() => {
			const directValue = paymentData.jejuSupport ?? paymentData.jeju_support
			if (directValue !== undefined && directValue !== null) {
				return directValue === 0 ? null : directValue
			}
			if (!allowFallbackForSupports) return null
			const fallback = contract?.domesticContract?.expenseReimbursementJeju ?? null
			return fallback === 0 ? null : fallback
		})(),
		overseasSupport: (() => {
			const directValue = paymentData.overseasSupport ?? paymentData.overseas_support
			if (directValue !== undefined && directValue !== null) {
				return directValue === 0 ? null : directValue
			}
			if (!allowFallbackForSupports) return null
			const fallback = contract?.domesticContract?.overseasSupport ?? null
			return fallback === 0 ? null : fallback
		})(),
		withholdingTaxAmount:
			paymentData.withholdingTaxAmount ??
			paymentData.weeklySalaryWithholdingTax ??
			paymentData.withholding_tax_amount ??
			null,
		caddieCommissionAmount: paymentData.caddieCommissionAmount ?? null,
		caddieVatAmount: paymentData.caddieVatAmount ?? null,
		caddieFinalReceiveAmount:
			paymentData.caddieFinalReceiveAmount ?? paymentData.totalPayment ?? null,
	}
}

const transformPaymentDetail = (detail, currentUser) => {
	if (!detail) return null

	const contractPaymentId = detail?.id
	const contract = detail?.contract ?? {}
	const contractTour = detail?.contractTour ?? {}
	const tour = contractTour?.tour ?? {}
	const player = detail?.player ?? contract?.player ?? {}
	const caddie = detail?.caddie ?? contract?.caddie ?? {}

	const contractType = contract?.contract_type
	const paymentStatus = PAYMENT_STATUS_MAP[detail?.paymentStatus] || '에러'
	const paymentType = detail?.payment_type
	const isTournamentContract = Boolean(detail?.contractTour) || paymentType === 'PER_GAME'

	const currentUserId = currentUser?.id
	const currentUserType = currentUser?.userType
	const isCurrentUserPlayer =
		(currentUserId && player?.id && currentUserId === player.id) ||
		currentUserType === 'tourPro'

	const counterpart = isCurrentUserPlayer ? caddie : player
	const counterpartTypeCode =
		counterpart?.user_type ||
		counterpart?.profile_type ||
		(isCurrentUserPlayer ? 'tourCaddie' : 'tourPro')

	const resolvePeriodTitle = (type) => {
		if (type === 'year_second_half') return '기간계약(하반기)'
		if (type === 'year_first_half') return '기간계약(상반기)'
		if (type === 'both') return '기간계약(상반기/하반기)'
		return ''
	}

	const type = resolveProfileTypeLabel(counterpartTypeCode)
	const counterpartName =
		counterpart?.name || (isCurrentUserPlayer ? caddie?.name : player?.name) || '-'

	const title =
		tour?.name ||
		resolvePeriodTitle(contractType) ||
		(contractType ? contractType : '정산 항목')

	const dateStart = tour?.started_at || contract?.contract_started_at
	const dateEnd = tour?.ended_at || contract?.contract_ended_at

	const formatDateString = (value) => {
		if (!value) return null
		const date = new Date(value)
		if (Number.isNaN(date.getTime())) return null
		const year = date.getFullYear()
		const month = String(date.getMonth() + 1).padStart(2, '0')
		const day = String(date.getDate()).padStart(2, '0')
		return `${year}-${month}-${day}`
	}

	const formattedStart = formatDateString(dateStart)
	const formattedEnd = formatDateString(dateEnd)

	const isPeriodContract =
		PERIOD_CONTRACT_TYPES.has(contractType) ||
		paymentType === 'PERIOD' ||
		!!detail?.periodSalary

	const normalizedContractPayment = {
		...detail,
		proTotalPayment:
			detail?.proTotalPayment ??
			detail?.proTotalPaymentAmount ??
			detail?.proFinalPaymentAmount ??
			null,
	}

	return {
		id: contractPaymentId,
		title,
		status: paymentStatus,
		statusCode: detail?.paymentStatus,
		paymentType,
		tourDate: formattedStart && formattedEnd ? `${formattedStart} ~ ${formattedEnd}` : '-',
		caddieName: caddie?.name || '',
		counterpartyName: counterpartName,
		counterpartyType: type,
		isPeriodContract,
		isTournamentContract,
		userRole: isCurrentUserPlayer ? 'pro' : 'caddie',
		counterpartyProfileImage:
			counterpart?.profile_image ||
			(counterpartTypeCode === 'tourPro' || counterpartTypeCode === 'TOURPRO'
				? '/images/pro-default.png'
				: '/images/caddie-default-profile.svg'),
		counterpartyUser: counterpart,
		original: detail,
		contractPayment: normalizedContractPayment,
		contract,
		contractTour,
		withholdingTaxPaid: Boolean(
			detail?.withholdingTaxPaid ?? detail?.withholding_tax_paid ?? false,
		),
	}
}

const CounterpartyBrief = ({ paymentItem }) => {
	if (!paymentItem) return null

	const counterpartUser = paymentItem.counterpartyUser ?? {}
	const fallbackTypeLabel = resolveProfileTypeLabel(
		counterpartUser?.user_type || counterpartUser?.profile_type,
	)
	const counterpartType = paymentItem.counterpartyType || fallbackTypeLabel || ''
	const counterpartName =
		paymentItem.counterpartyName || counterpartUser?.name || paymentItem.caddieName || '-'
	const counterpartProfileImage = paymentItem.counterpartyProfileImage
	const fallbackImage =
		counterpartType === '투어프로'
			? '/images/pro-default.png'
			: '/images/caddie-default-profile.svg'

	const caddieExtra = counterpartUser?.user_tour_caddie ?? {}
	const proExtra = counterpartUser?.user_tour_pro ?? {}
	const teamName =
		caddieExtra?.caddie_team_name ||
		counterpartUser?.team_name ||
		counterpartUser?.caddie_team_name ||
		''
	const careerLevel =
		caddieExtra?.career_level || proExtra?.career_level || counterpartUser?.career_level || ''

	const careerTextMap = {
		NEWBIE: '신입',
		ONE_TO_THREE: '1~3년차',
		FOUR_TO_SIX: '4~6년차',
		SEVEN_TO_TEN: '7~10년차',
		OVER_TEN: '10년 이상',
	}
	const careerText = careerLevel ? careerTextMap[careerLevel] : ''

	const detailLineParts = [counterpartType, counterpartName]
	if (careerText) detailLineParts.push(careerText)
	if (teamName) detailLineParts.push(teamName)
	const detailLine = detailLineParts.filter(Boolean).join(' / ')

	return (
		<div className={styles.counterpartyBrief}>
			{/* <div className={styles.counterpartyBriefHeader}>프로필 상세</div> */}
			<div className={styles.counterpartyBriefBody}>
				<div className={styles.counterpartyAvatarWrapper}>
					<img
						src={counterpartProfileImage || fallbackImage}
						alt="counterparty profile"
						onError={(event) => {
							event.currentTarget.onerror = null
							event.currentTarget.src = fallbackImage
						}}
					/>
				</div>
				<div className={styles.counterpartyInfoTexts} title={detailLine}>
					<span className={styles.counterpartyName}>{counterpartName}</span>
					{(counterpartType || careerText || teamName) && (
						<span className={styles.counterpartyMeta}>
							{[counterpartType, careerText, teamName].filter(Boolean).join(' · ')}
						</span>
					)}
				</div>
			</div>
		</div>
	)
}

const PaymentManageDetailPage = () => {
	const { contractPaymentId } = useParams()
	const navigate = useNavigate()
	const [isDepositDefaultModalOpen, setIsDepositDefaultModalOpen] = useState(false)
	const { data: currentUser } = useUser()
	const { data, isLoading, isError } = useSettlementDetail(contractPaymentId)
	const { data: bankAccountData, isLoading: isBankAccountLoading } = useBankAccount()
	const disburseMutation = useDisburseSettlement()

	const paymentItem = useMemo(
		() => transformPaymentDetail(data, currentUser),
		[data, currentUser],
	)

	const amountInfo = useMemo(
		() =>
			buildAmountInfo(
				paymentItem?.original,
				paymentItem?.contract,
				paymentItem?.contractTour,
			),
		[paymentItem?.original, paymentItem?.contract, paymentItem?.contractTour],
	)
	const statusDetailText = useMemo(
		() => getStatusDetailMessage(paymentItem?.status),
		[paymentItem?.status],
	)
	const isPeriodContract = paymentItem?.isPeriodContract ?? false

	const virtualAccountInfo = useMemo(() => {
		const detail = paymentItem?.original

		if (!detail) return null

		const issued = Boolean(
			detail.virtualAccountIssued ?? detail.virtual_account_issued ?? false,
		)
		const bankCode =
			detail.virtualAccountBankCode ??
			detail.virtual_account_bank_code ??
			detail.virtualAccount?.bankCode ??
			null
		const accountNumber =
			detail.virtualAccountNumber ??
			detail.virtual_account_number ??
			detail.virtualAccount?.accountNumber ??
			'-'
		const expireRaw =
			detail.virtualAccountExpireAt ??
			detail.virtual_account_expire_at ??
			detail.virtualAccount?.expireDate ??
			null

		const expireDate = expireRaw ? new Date(expireRaw) : null
		const now = new Date()
		const isExpired =
			!expireDate || Number.isNaN(expireDate.getTime())
				? true
				: expireDate.getTime() < now.getTime()

		return {
			isIssued: issued,
			bankCode,
			bankName: bankCode ? (BANK_CODE_LABEL_MAP[bankCode] ?? bankCode) : '-',
			accountNumber: accountNumber || '-',
			expireRaw,
			expireText: formatExpireDeadline(expireRaw),
			isExpired,
		}
	}, [paymentItem?.original])

	const shouldDisableDepositButton =
		paymentItem?.status === '입금대기' &&
		virtualAccountInfo?.isIssued &&
		virtualAccountInfo?.isExpired === false

	const shouldShowVirtualAccountSection =
		paymentItem?.status === '입금대기' && virtualAccountInfo?.isIssued

	const hasBankAccount =
		Boolean(bankAccountData?.bankName) &&
		Boolean(bankAccountData?.accountNumber) &&
		Boolean(bankAccountData?.ownerName)

	const isCaddieUser = paymentItem?.userRole === 'caddie'

	const shouldShowBankAccountAlert = isCaddieUser && !isBankAccountLoading && !hasBankAccount

	const handleRequestPayout = () => {
		if (!hasBankAccount || disburseMutation.isPending || !paymentItem?.id) return

		disburseMutation
			.mutateAsync({ contractPaymentId: paymentItem.id })
			.then(() => {
				alert('지급이 완료되었습니다.')
			})
			.catch((error) => {
				console.error('정산 지급 요청 실패:', error)
				alert('정산 지급 요청에 실패했어요. 잠시 후 다시 시도해 주세요.')
			})
	}

	const displaySections = useMemo(() => {
		if (!paymentItem) return []

		const sections = []
		const formatAmount = (value, { negative = false } = {}) => {
			if (value === null || value === undefined) return '-'

			const number = Number(value)
			if (Number.isNaN(number)) return value

			const absolute = Math.abs(number)
			const formatted = `${formatNumber(absolute)} 원`

			if (negative || number < 0) {
				if (absolute === 0) return '-'
				return `- ${formatted}`
			}

			return formatted
		}
		const hasMeaningfulValue = (value) => value !== null && value !== undefined && value !== '-'
		const addSection = (section) => {
			if (section.items && section.items.length > 0) {
				sections.push(section)
			}
		}
		const toNumberOrNull = (value) => {
			const num = Number(value)
			return Number.isFinite(num) ? num : null
		}

		const { userRole, isTournamentContract } = paymentItem

		if (userRole === 'pro' && isTournamentContract) {
			const isSalaryTaxIncluded =
				paymentItem.contract?.domesticContract?.isSalaryTaxIncluded ?? false
			const withholdingTaxPaid = Boolean(paymentItem.withholdingTaxPaid)

			const baseItems = [
				{
					key: 'weeklySalary',
					label: '주급',
					value: formatAmount(amountInfo.weeklySalaryPayable),
					meta: [
						hasMeaningfulValue(formatAmount(amountInfo.weeklySalaryBase))
							? `${isSalaryTaxIncluded ? '세후' : '세전'} ${formatAmount(amountInfo.weeklySalaryBase)}`
							: null,
					]
						.filter(Boolean)
						.join(' · '),
				},
				{
					key: 'jejuSupport',
					label: '제주도 지원금',
					value: formatAmount(amountInfo.jejuSupport),
				},
				{
					key: 'overseasSupport',
					label: '해외 대회 지원금',
					value: formatAmount(amountInfo.overseasSupport),
				},
			].filter((item) => hasMeaningfulValue(item.value))

			addSection({
				key: 'base',
				title: '기본급',
				description: '계약 주급은 세전 금액, 주급은 세후 입금 예정 금액입니다.',
				items: baseItems,
			})

			if (hasMeaningfulValue(amountInfo.totalPrize)) {
				const incentiveMetaParts = [`총상금 ${formatAmount(amountInfo.totalPrize)}`]
				const rank = paymentItem.contractTour?.rank
				if (rank !== null && rank !== undefined) {
					incentiveMetaParts.push(`${rank}위`)
				}
				const madeCut = paymentItem.contractTour?.made_cut
				if (madeCut !== null && madeCut !== undefined) {
					incentiveMetaParts.push(madeCut ? '컷통과' : '컷탈락')
				}

				const incentiveItems = [
					{
						key: 'caddieIncentive',
						label: '캐디 인센티브',
						value: formatAmount(amountInfo.incentive),
						meta: [
							// paymentItem.original?.incentiveRate != null
							// 	? `인센티브 ${paymentItem.original.incentiveRate}%`
							// 	: null,
							// '공제전',
							`${paymentItem.original?.incentiveType === 'displayed' ? '상금 표기액' : '실수령액'}의 ${paymentItem.original?.incentiveRate ?? 0}%`,
						]
							.filter(Boolean)
							.join(' · '),
					},
				].filter((item) => hasMeaningfulValue(item.value))

				addSection({
					key: 'incentive',
					title: '인센티브',
					subtitle: incentiveMetaParts.filter(Boolean).join(' · '),
					description: '대회 성적에 따른 인센티브 내역입니다.',
					items: incentiveItems,
				})
			}

			const feeItems = [
				{
					key: 'spoeumFee',
					label: '스포이음 수수료',
					value: formatAmount(amountInfo.proCommissionAmount),
					// meta:
					// 	paymentItem.contract?.proCommission != null
					// 		? `계약 ${paymentItem.contract.proCommission}%`
					// 		: undefined,
				},
				{
					key: 'spoeumVat',
					label: '수수료 부가세',
					value: formatAmount(amountInfo.proVat),
				},
			].filter((item) => hasMeaningfulValue(item.value))

			addSection({
				key: 'fee',
				title: '스포이음 수수료',
				description: '정산 시 공제되는 플랫폼 수수료입니다.',
				items: feeItems,
			})

			if (withholdingTaxPaid) {
				const withholdingItems = [
					{
						key: 'withholdingTax',
						label: '원천세 대납액',
						value: formatAmount(amountInfo.withholdingTaxAmount),
					},
				].filter((item) => hasMeaningfulValue(item.value))

				addSection({
					key: 'withholding',
					title: '원천세 대납',
					description: '스포이음이 대신 납부하는 원천세 금액입니다.',
					items: withholdingItems,
				})
			}

			const baseTotalNumber = toNumberOrNull(amountInfo.proTotalPayment)
			const withholdingNumber = toNumberOrNull(amountInfo.withholdingTaxAmount)
			const totalCombinedNumber =
				withholdingTaxPaid && baseTotalNumber !== null
					? baseTotalNumber + (withholdingNumber ?? 0)
					: baseTotalNumber
			const totalCombinedValue = formatAmount(totalCombinedNumber)

			if (hasMeaningfulValue(amountInfo.proTotalPayment)) {
				addSection({
					key: 'total',
					title: '총 입금액',
					type: 'total',
					items: [
						{
							key: 'totalAmount',
							label: '총 입금액',
							value: totalCombinedValue,
						},
					],
				})
			}

			return sections
		}

		if (userRole === 'caddie' && isTournamentContract) {
			const isSalaryTaxIncluded =
				paymentItem.contract?.domesticContract?.isSalaryTaxIncluded ?? false

			const baseItems = [
				{
					key: 'weeklySalary',
					label: '주급',
					value: formatAmount(amountInfo.weeklySalaryPayable),
					meta: [
						hasMeaningfulValue(formatAmount(amountInfo.weeklySalaryBase))
							? `${isSalaryTaxIncluded ? '세후' : '세전'} ${formatAmount(amountInfo.weeklySalaryBase)}`
							: null,
					]
						.filter(Boolean)
						.join(' · '),
				},
				{
					key: 'jejuSupport',
					label: '제주도 지원금',
					value: formatAmount(amountInfo.jejuSupport),
				},
				{
					key: 'overseasSupport',
					label: '해외 대회 지원금',
					value: formatAmount(amountInfo.overseasSupport),
				},
			].filter((item) => hasMeaningfulValue(item.value))

			addSection({
				key: 'base',
				title: '기본급',
				description: '계약 주급은 세전 금액, 주급은 세후 입금 예정 금액입니다.',
				items: baseItems,
			})

			if (hasMeaningfulValue(amountInfo.totalPrize)) {
				const incentiveMetaParts = [`총상금 ${formatAmount(amountInfo.totalPrize)}`]
				const rank = paymentItem.contractTour?.rank
				if (rank !== null && rank !== undefined) {
					incentiveMetaParts.push(`${rank}위`)
				}
				const madeCut = paymentItem.contractTour?.made_cut
				if (madeCut !== null && madeCut !== undefined) {
					incentiveMetaParts.push(madeCut ? '컷통과' : '컷탈락')
				}

				const incentiveItems = [
					{
						key: 'caddieIncentive',
						label: '캐디 인센티브',
						value: formatAmount(amountInfo.incentive),
						meta: [
							`${paymentItem.original?.incentiveType === 'displayed' ? '상금 표기액' : '실수령액'}의 ${paymentItem.original?.incentiveRate ?? 0}%`,
						]
							.filter(Boolean)
							.join(' · '),
					},
				].filter((item) => hasMeaningfulValue(item.value))

				addSection({
					key: 'incentive',
					title: '인센티브',
					subtitle: incentiveMetaParts.filter(Boolean).join(' · '),
					description: '대회 성적에 따른 인센티브 내역입니다.',
					items: incentiveItems,
				})
			}

			const feeItems = [
				{
					key: 'caddieFee',
					label: '캐디 수수료',
					value: formatAmount(amountInfo.caddieCommissionAmount, { negative: true }),
				},
				{
					key: 'caddieVat',
					label: '수수료 부가세',
					value: formatAmount(amountInfo.caddieVatAmount, { negative: true }),
				},
			].filter((item) => hasMeaningfulValue(item.value))

			addSection({
				key: 'fee',
				title: '스포이음 수수료',
				description: '정산 시 공제되는 플랫폼 수수료입니다.',
				items: feeItems,
			})

			if (hasMeaningfulValue(amountInfo.caddieFinalReceiveAmount)) {
				addSection({
					key: 'total',
					title: '총 지급액',
					type: 'total',
					items: [
						{
							key: 'totalAmount',
							label: '총 지급액',
							value: formatAmount(amountInfo.caddieFinalReceiveAmount),
						},
					],
				})
			}

			return sections
		}

		// Fallback display using status-based logic
		const status = paymentItem.status

		if (status === '입금대기') {
			const totalLabel = paymentItem.userRole === 'pro' ? '총 입금액' : '총지급액'
			let totalValue = formatAmount(amountInfo.proTotalPayment)

			if (isPeriodContract) {
				const withholdingTaxPaid = Boolean(paymentItem.withholdingTaxPaid)
				const baseTotalNumber = toNumberOrNull(amountInfo.proTotalPayment)
				const withholdingNumber = toNumberOrNull(amountInfo.withholdingTaxAmount)
				const totalCombinedNumber =
					withholdingTaxPaid && baseTotalNumber !== null
						? baseTotalNumber + (withholdingNumber ?? 0)
						: baseTotalNumber
				totalValue = formatAmount(totalCombinedNumber)

				addSection({
					key: 'base',
					title: '정산 항목',
					items: [
						{
							key: 'periodSalary',
							label: '기간급여액',
							value: formatAmount(amountInfo.periodSalary),
						},
						{
							key: 'proCommission',
							label: '수수료',
							value: formatAmount(amountInfo.proCommission),
						},
					],
				})
			} else {
				addSection({
					key: 'base',
					title: '정산 항목',
					items: [
						{
							key: 'totalPrize',
							label: '총상금',
							value: formatAmount(amountInfo.totalPrize),
						},
						{
							key: 'totalPayment',
							label: '실수령액',
							value: formatAmount(amountInfo.totalPayment),
						},
						{
							key: 'weeklySalary',
							label: '주급',
							value: formatAmount(amountInfo.weeklySalary),
						},
						{
							key: 'incentive',
							label: '인센티브',
							value: formatAmount(amountInfo.incentive),
						},
						{
							key: 'proCommission',
							label: '수수료',
							value: formatAmount(amountInfo.proCommission),
						},
						{
							key: 'jejuSupport',
							label: '제주도 지원금',
							value: formatAmount(amountInfo.jejuSupport),
						},
						{
							key: 'overseasSupport',
							label: '해외 대회 지원금',
							value: formatAmount(amountInfo.overseasSupport),
						},
					].filter((item) => hasMeaningfulValue(item.value)),
				})
			}

			addSection({
				key: 'total',
				title: totalLabel,
				type: 'total',
				items: [
					{
						key: 'totalAmount',
						label: totalLabel,
						value: totalValue,
					},
				],
			})

			return sections
		}

		return sections
	}, [
		amountInfo.jejuSupport,
		amountInfo.overseasSupport,
		amountInfo.periodSalary,
		amountInfo.proCommission,
		amountInfo.proCommissionAmount,
		amountInfo.proTotalPayment,
		amountInfo.proVat,
		amountInfo.caddieCommissionAmount,
		amountInfo.caddieVatAmount,
		amountInfo.caddieFinalReceiveAmount,
		amountInfo.totalPayment,
		amountInfo.totalPrize,
		amountInfo.weeklySalary,
		amountInfo.weeklySalaryBase,
		amountInfo.weeklySalaryPayable,
		amountInfo.incentive,
		amountInfo.withholdingTaxAmount,
		paymentItem,
	])

	const handleGoBack = () => {
		navigate('/settlements')
	}

	if (isLoading) {
		return (
			<div className={styles.paymentManageDetailPage}>
				<div className={styles.loadingState}>정산 정보를 불러오는 중입니다.</div>
			</div>
		)
	}

	if (isError || !paymentItem) {
		return (
			<div className={styles.paymentManageDetailPage}>
				<div className={styles.errorState}>
					<p>정산 상세 정보를 불러오지 못했습니다.</p>
					<button className={styles.goBackButton} onClick={handleGoBack}>
						목록으로 돌아가기
					</button>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.paymentManageDetailPage}>
			{isDepositDefaultModalOpen && (
				<DepositDefaultModal
					item={paymentItem}
					onClose={() => setIsDepositDefaultModalOpen(false)}
				/>
			)}
			<div className={styles.goBackWrapper}>
				<div className={styles.goBackContainer} onClick={handleGoBack}>
					<ArrowLeftIcon className={styles.goBackIcon} />
					<span className={styles.goBackText}>뒤로</span>
				</div>
			</div>
			<span className={styles.statusTitle}>정산현황</span>
			<PaymentStatus status={paymentItem.status} />
			<span className={styles.statusDetailText}>{statusDetailText}</span>
			<TourDisplay
				tourName={paymentItem.title}
				caddieName={paymentItem.counterpartyName || paymentItem.caddieName}
				tourDate={paymentItem.tourDate}
			/>
			<div className={styles.space_1}></div>
			<CounterpartyBrief paymentItem={paymentItem} />
			<div className={styles.space_2}></div>
			<div className={styles.adjustContentWrapper}>
				{shouldShowBankAccountAlert && (
					<div className={styles.bankAccountAlert}>
						<div className={styles.bankAccountAlertIcon}>
							<span>!</span>
						</div>
						<div className={styles.bankAccountAlertTexts}>
							{/* <span className={styles.bankAccountAlertTitle}>계좌 정보가 필요해요</span> */}
							<span className={styles.bankAccountAlertDescription}>
								계좌를 입력해야 정산 금액을 입금받을 수 있어요!
							</span>
						</div>
						<Link to="/myinfo/bank-account" className={styles.bankAccountAlertButton}>
							계좌번호 입력하기
						</Link>
					</div>
				)}
				<div className={styles.adjustContentHeader}>
					<div className={styles.adjustContentHeaderleft}>
						<span className={styles.adjustContentTitle}>정산내역</span>
						{/* {paymentItem.status !== '정산전' && (
							<span className={styles.adjustDetail}>자세히 보기</span>
						)} */}
					</div>
					<div className={styles.adjustContentHeaderRight}>
						{paymentItem.status === '정산전' && (
							<span className={styles.beforeAdjust}>정산전</span>
						)}
						{paymentItem.status === '입금대기' && (
							<button
								className={clsx(styles.depositButton, {
									[styles.depositButtonDisabled]: shouldDisableDepositButton,
								})}
								disabled={shouldDisableDepositButton}
								onClick={() => setIsDepositDefaultModalOpen(true)}>
								입금하기
							</button>
						)}
						{paymentItem.status === '입금완료' &&
							(isCaddieUser ? (
								<button
									className={clsx(styles.depositButton, {
										[styles.depositButtonDisabled]:
											!hasBankAccount || disburseMutation.isPending,
									})}
									disabled={!hasBankAccount || disburseMutation.isPending}
									onClick={handleRequestPayout}>
									정산 요청
								</button>
							) : (
								<span className={styles.depositFinished}>입금완료</span>
							))}
						{paymentItem.status === '지급완료' && (
							<span className={styles.paymentFinished}>지급완료</span>
						)}
					</div>
				</div>
				{shouldShowVirtualAccountSection && (
					<div className={styles.virtualAccountInfoBox}>
						<div className={styles.virtualAccountInfoRow}>
							<span className={styles.virtualAccountLabel}>입금 은행</span>
							<span className={styles.virtualAccountValue}>
								{virtualAccountInfo?.bankName ?? '-'}
							</span>
						</div>
						<div className={styles.virtualAccountInfoRow}>
							<span className={styles.virtualAccountLabel}>입금 계좌</span>
							<span className={styles.virtualAccountValue}>
								{virtualAccountInfo?.accountNumber ?? '-'}
							</span>
						</div>
						<div className={styles.virtualAccountInfoRow}>
							<span className={styles.virtualAccountLabel}>입금 기한</span>
							<span
								className={clsx(
									styles.virtualAccountValue,
									virtualAccountInfo?.isExpired && styles.virtualAccountExpired,
								)}>
								{virtualAccountInfo?.expireText}
								{virtualAccountInfo?.isExpired ? ' (만료)' : ''}
							</span>
						</div>
					</div>
				)}
				<div className={styles.adjustContentBody}>
					{paymentItem.status === '정산전' ? (
						<div className={styles.emptyStateInfo}>
							<div className={styles.emptyStateIcon}>
								<BellAlertIcon className={styles.emptyStateIconSvg} />
							</div>
							<span className={styles.emptyStateTitle}>정산을 준비 중이에요</span>
							<p className={styles.emptyStateDescription}>
								스포이음에서 정산이 완료되면 바로 알려드릴게요.
							</p>
						</div>
					) : (
						displaySections.map((section) => (
							<div
								key={section.key}
								className={clsx(
									styles.detailSection,
									section.type === 'total' && styles.detailSectionTotal,
								)}>
								{section.type !== 'total' && (
									<div className={styles.detailSectionHeader}>
										<div className={styles.detailSectionTitleRow}>
											<span className={styles.detailSectionTitle}>
												{section.title}
											</span>
											{section.subtitle && (
												<span className={styles.detailSectionSubtitle}>
													{section.subtitle}
												</span>
											)}
										</div>
									</div>
								)}
								<div className={styles.detailSectionBody}>
									{section.items.map((item) => (
										<div key={item.key} className={styles.detailSectionRow}>
											<span className={styles.detailSectionLabel}>
												{item.label}
											</span>
											<div className={styles.detailSectionValue}>
												<span className={styles.detailSectionAmount}>
													{item.value}
												</span>
												{item.meta && (
													<span className={styles.detailSectionMeta}>
														{item.meta}
													</span>
												)}
											</div>
										</div>
									))}
								</div>
							</div>
						))
					)}
				</div>
			</div>
		</div>
	)
}

export default PaymentManageDetailPage
